import { Home, Reset } from "@carbon/icons-react";
import { cn } from "@heroui/react";
import type React from "react";
import { useTranslation } from "react-i18next";
import type { SlotsToClasses } from "src/utils/componentUtils";
import { Alert } from "../components/common/Alert/Alert";
import SemanticButtons from "../components/common/Button/SemanticButtons";

export interface ErrorViewProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  message?: React.ReactNode;
  className?: string;
  classNames?: SlotsToClasses<
    | "base"
    | "title"
    | "description"
    | "buttons"
    | "reloadButton"
    | "homeButton"
    | "errorDetails"
  >;
}

/** Fallback view for displaying error messages when something goes wrong on the page */
export const ErrorView = ({
  title,
  description,
  message,
  className,
  classNames,
}: ErrorViewProps) => {
  const { t } = useTranslation();

  const displayTitle = title ?? t("error::Something went wrong");
  const displayDescription =
    description ??
    t(
      "error::An unexpected error occurred. Please try reloading the page. If the problem persists, contact support.",
    );
  const displayMessage =
    message ?? t("error::No additional error information available.");

  return (
    <div
      data-testid="error-view"
      className={cn("flex flex-col gap-4", className, classNames?.base)}
    >
      <h1 className={cn("text-4xl font-bold text-warning", classNames?.title)}>
        {displayTitle}
      </h1>
      <p className={cn("text-lg text-muted", classNames?.description)}>
        {displayDescription}
      </p>
      <div className={cn("w-fit flex gap-4", classNames?.buttons)}>
        <SemanticButtons
          primary={{
            onPress: () => window.location.reload(),
            color: "warning",
            className: cn(classNames?.reloadButton),
            label: t("error::Reload Page"),
            icon: Reset,
          }}
          secondary={{
            href: "/",
            color: "default",
            className: cn(classNames?.homeButton),
            label: t("error::Back to Home"),
            icon: Home,
          }}
        />
      </div>

      <details
        className={cn("mt-4 whitespace-pre-wrap", classNames?.errorDetails)}
      >
        <summary className="text-muted cursor-pointer">
          {t("error::Error Details")}
        </summary>
        <Alert className="mt-2" status="warning">
          {displayMessage}
        </Alert>
      </details>
    </div>
  );
};
